import React, { useState } from "react";

import {
  faBottleWater,
  faDollar,
  faRuler,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Container, Image, Modal, Row } from "react-bootstrap";
import Gallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { Navigate, useParams } from "react-router-dom";

import { formatMoney } from "@/utils/Toolbox";

import ContainerDivider from "../components/ContainerDivider.component";
import DocumentComponent from "../components/Document.component";
import HeroComponent from "../components/Hero.component";
import PremiumUpgradesComponent from "../components/PremiumUpgrades.component";
import StandardFeaturesComponent from "../components/StandardFeatures.component";
import models from "../data/models.json";
import i18n from "../i18n";

import type { IGalleryImage } from "@/interfaces/IGalleryImage";
import type { IImage } from "@/interfaces/IImage";

// Variant
interface IModel {
  name: string;
  url_name: string;
  size: string;
  price: number;
  bottles: number;
  description: string;
  slug: string;
  hero: string;
  img: string;
  footprint: number;
  total_living_space: number;
  sizes?: { label: string; value: number }[];
  size_with_note?: { label: string; value: number; note: string };
}

const Model = () => {
  const { t } = i18n;
  const params = useParams();

  const modelKey = (params.model ?? "").toLowerCase();
  const model = models.find((item: any) => item.key.toLowerCase() === modelKey);
  const [showGallery, setShowGallery] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  if (model === undefined) {
    return <Navigate to="/models" />;
  }

  const openGallery = (index: number) => {
    setCurrentIndex(index);
    setShowGallery(true);
  };

  // Every photo has a downscaled sibling in a `thumbs/` folder; the
  // full-size file is only worth fetching for the fullscreen modal.
  const thumbSrc = (src: string) => src.replace(/\/([^/]+)$/, "/thumbs/$1");

  const images: IGalleryImage[] = model.images.map((item: IImage) => ({
    original: item.src,
    thumbnail: thumbSrc(item.src),
    description: item.alt,
    alt: item.alt,
  }));

  const renderThumbnails = (start = 0, end = 0) => {
    const items = model.images.slice(start, end);

    if (items.length === 3) {
      if (["pakalps", "paktokyo"].includes(modelKey) && start % 3 === 0) {
        return (
          <Row>
            {items.map((item: IImage, index: number) => (
              <Col
                key={index}
                className={`mb-3 ${index === 0 && start === 0 ? "d-none d-lg-block" : ""}`}
                xs={index === 0 ? "12" : "6"}
              >
                <Image
                  rounded
                  alt={item.alt}
                  className="mw-100"
                  loading="lazy"
                  onClick={() => openGallery(start + index)}
                  role="button"
                  src={thumbSrc(item.src)}
                />
              </Col>
            ))}
          </Row>
        );
      }

      let orderClass =
        modelKey === "pakstockholm" ? "order-lg-2" : "order-lg-1";
      if (start === 0) {
        orderClass = modelKey === "pakstockholm" ? "order-lg-1" : "order-lg-2";
      }

      return (
        <Row>
          {/* render first image in the batch */}
          <Col
            className={`text-center mb-4 ${orderClass} d-none d-lg-block`}
            lg={8}
            xs={12}
          >
            <Image
              rounded
              alt={items[0].alt}
              className="mw-100"
              loading="lazy"
              onClick={() => openGallery(start)}
              role="button"
              src={thumbSrc(items[0].src)}
            />
          </Col>

          <Col className="text-center order-lg-1" lg={4} xs={12}>
            <Row>
              {items.map((item: IImage, index: number) => {
                // skip the first image
                if (index === 0) {
                  return <span key={index} />;
                }

                return (
                  <Col key={index} className="mb-3" lg={12} xs={6}>
                    <Image
                      rounded
                      alt={item.alt}
                      className="mw-100"
                      loading="lazy"
                      onClick={() => openGallery(start + index)}
                      role="button"
                      src={thumbSrc(item.src)}
                    />
                  </Col>
                );
              })}
            </Row>
          </Col>
        </Row>
      );
    }

    return (
      <Row>
        {items.map((item: IImage, index: number) => (
          <Col
            key={index}
            className={`mb-3 ${index === 0 && start === 0 ? "d-none d-lg-block" : ""}`}
            xs={items.length === 1 ? 12 : 6}
          >
            <Image
              rounded
              alt={item.alt}
              className="mw-100"
              loading="lazy"
              onClick={() => openGallery(start + index)}
              role="button"
              src={thumbSrc(item.src)}
            />
          </Col>
        ))}
      </Row>
    );
  };

  const primaryVariant = model.variants[0] as IModel;

  return (
    <React.Fragment>
      <DocumentComponent og={model.og} title={model.name} />

      <HeroComponent title={model.name} />

      <Modal
        fullscreen
        className="modalGallery"
        data-bs-theme="dark"
        onHide={() => setShowGallery(false)}
        show={showGallery}
      >
        <Modal.Header closeButton />
        <Modal.Body>
          <Gallery
            items={images}
            showThumbnails={false}
            startIndex={currentIndex}
          />
        </Modal.Body>
      </Modal>

      <Container>
        <Row className="text-center mb-4 modelSummaryContainer">
          <Col className="text-center mb-4 d-block d-lg-none" xs={12}>
            <Image
              rounded
              alt={model.images[0].alt}
              className="mw-100"
              onClick={() => openGallery(0)}
              role="button"
              src={thumbSrc(model.images[0].src)}
            />
          </Col>

          <Col className="order-2 order-lg-1" lg={4} xs={12}>
            <FontAwesomeIcon
              color="var(--info-color)"
              icon={faDollar}
              size="2x"
            />
            <p className="modelSubHeader justify-content-center">
              {t("starting_from_price", {
                price: formatMoney(primaryVariant.price),
              })}
            </p>
          </Col>

          <Col className="order-1 order-lg-2" lg={4} xs={12}>
            <FontAwesomeIcon
              color="var(--info-color)"
              icon={faBottleWater}
              size="2x"
            />
            <p className="modelSubHeader justify-content-center">
              {t("made_from_x_recycled_water_bottles", {
                x: primaryVariant.bottles
                  ? primaryVariant.bottles.toLocaleString()
                  : 0,
              })}
            </p>
          </Col>

          <Col className="order-3 order-lg-3 modelSizeContainer" lg={4} xs={12}>
            <FontAwesomeIcon
              color="var(--info-color)"
              icon={faRuler}
              size="2x"
            />
            {primaryVariant.sizes ? (
              primaryVariant.sizes.map((modelSize, index) => (
                <p
                  key={index}
                  className="modelSubHeader justify-content-center mb-0"
                >
                  {t(modelSize.label)}:&nbsp;{modelSize.value}&nbsp;
                  {t("sq_ft")}
                </p>
              ))
            ) : primaryVariant.size_with_note ? (
              <React.Fragment>
                <p className="modelSubHeader justify-content-center m-0">
                  {t(primaryVariant.size_with_note.label)}:&nbsp;
                  {primaryVariant.size_with_note.value}&nbsp;{t("sq_ft")}
                </p>
                <p className="modelSubHeader justify-content-center">
                  ({t(primaryVariant.size_with_note.note)})
                </p>
              </React.Fragment>
            ) : (
              <p className="modelSubHeader justify-content-center">
                {t("size")}: {primaryVariant.footprint} {t("sq_ft")}
              </p>
            )}
          </Col>
        </Row>

        <Row>
          {model.descriptions.map((description: string, index: number) => (
            <p key={index}>{t(description)}</p>
          ))}
        </Row>

        {modelKey === "pakalps" ? (
          <React.Fragment>
            {renderThumbnails(0, 3)}
            {renderThumbnails(3, 6)}
            {renderThumbnails(6, 9)}
          </React.Fragment>
        ) : modelKey === "paktokyo" ? (
          <React.Fragment>
            {renderThumbnails(0, 3)}
            {renderThumbnails(3, 4)}
            {renderThumbnails(4, 6)}
            {renderThumbnails(6, 7)}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {renderThumbnails(0, 3)}
            {renderThumbnails(3, 5)}
            {renderThumbnails(5, 8)}
          </React.Fragment>
        )}
      </Container>

      <Container>
        <Row>
          {model.variants.map((variant: any, index: number) => {
            // Add to the gallery
            images.push({
              original: variant.img,
              thumbnail: variant.img,
              description: variant.name,
              alt: variant.name,
            });

            let span = 4;

            if ([1, 2].includes(model.variants.length)) {
              span = 12;
            }

            return (
              <Col key={index} className="text-center mb-4" lg={span}>
                <Row>
                  <Col className="order-2 order-lg-1" xs={12}>
                    <div
                      className="rounded"
                      style={{ backgroundColor: "#CCCCCC" }}
                      onClick={() =>
                        openGallery(
                          images.length - model.variants.length + index,
                        )
                      }
                    >
                      <Image
                        rounded
                        alt={variant.name}
                        className="mw-100"
                        loading="lazy"
                        role="button"
                        src={variant.img}
                      />
                    </div>
                  </Col>

                  {model.variants.length > 1 && (
                    <Col className="mt-4 order-1 order-lg-2" xs={12}>
                      <h2
                        className="itemHeader"
                        style={{ color: "var(--bg-dark-color)" }}
                      >
                        {t(variant.name)}
                      </h2>
                      <p className="mb-1">
                        {t("total_living_space")}: {variant.total_living_space}{" "}
                        {t("sq_ft")}
                      </p>
                      <p>
                        {t("footprint")}: {variant.footprint} {t("sq_ft")}
                      </p>
                    </Col>
                  )}
                </Row>
              </Col>
            );
          })}
        </Row>
      </Container>

      <ContainerDivider title={t("standard_features")} />

      <StandardFeaturesComponent
        modelName={primaryVariant.name}
        standardFeatures={model.standard_features}
      />

      <ContainerDivider title={t("premium_upgrades")} />

      <PremiumUpgradesComponent />
    </React.Fragment>
  );
};

export default Model;
